import React, { useCallback, useState } from "react";
import styled from "styled-components";
import TextField from "../Common/TextField";
import womanDoctor from "../../SVGs/womanDoctor.svg";
import CustomButton from "../Common/CustomButton";
import {
  getApiClient,
  makeStandardApiErrorHandler,
} from "../../server/get_api_client";
import { useLocation } from "react-router-dom";
import RequireLogout from "../../permissions.js/RequireLogout";
import Footer from "../Common/Footer";
import Nav from "../Common/Nav";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import PageLoader from "../Common/PageLoader";

function SetupProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState(location.state?.username ?? "");
  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [strength, setStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    specialChar: false,
  });


  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&]).{8,}$/;
    return passwordRegex.test(password);
  };

  const evaluateStrength = (password) => {
    let score = 0;
    const requirements = { ...passwordRequirements };

    if (password.length >= 8) {
      score += 25;
      requirements.minLength = true;
    } else {
      requirements.minLength = false;
    }

    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) {
      score += 50;
      requirements.uppercase = true;
    } else {
      requirements.uppercase = false;
    }

    if (/[@$!%*?&]/.test(password)) {
      score += 25;
      requirements.specialChar = true;
    } else {
      requirements.specialChar = false;
    }

    setPasswordRequirements(requirements);
    setStrength(score);
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    evaluateStrength(value);
  };

  const Submit = useCallback(() => {
    if (!username || !validatePassword(password)) {
      toast.error(
        "Ensure the password meets the requirements and the username is filled."
      );
      return;
    }

    if (verifyPassword !== password) {
      toast.error("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    getApiClient()
      .ValidateRegister(
        location.state.source,
        username,
        password,
        location.state.contactId,
        location.state.code
      )
      .then((res) => {
        Cookies.set("hippocratic_portal_token", res.data.token, {
          expires: 1 / 24,
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("hippocratic_portal_contactid", location.state.contactId, {
          expires: 1 / 24,
          secure: true,
          sameSite: "Strict",
        });
        navigate("/MyInformation");
        window.location.reload();
      })
      .catch(makeStandardApiErrorHandler((error) => {
        setIsLoading(false);
        toast.error(error);
      }));
  }, [username, password, verifyPassword, location.state, navigate]);

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <Nav />
      <Container>
        <InnerContainer>
          <LeftContaienr>
            <LeftContaienrBanner>
              {location.state?.username &&
                <>
                  <Title>FINiSH Up YOUR ACCOUNT TRANSITION</Title>


                </>

              }
              {!location.state?.username &&
                <>
                  <Title>SIGN UP</Title>

                </>

              }

            </LeftContaienrBanner>
            <LeftContaienrContent>
              {location.state?.username &&
                <>
                  <Text>Your account data has been securely transferred from your previous portal.</Text>
                  <Text>Please create your new username and password to continue:</Text>
                </>

              }
              {!location.state?.username &&
                <>
                  <Text>Setup your username and password:</Text>

                </>

              }
              <FieldsContainer>
                <TextField
                  title={"*Username/Email:"}
                  direction={"column"}
                  type={"text"}
                  width={"100%"}
                  value={username}
                  setValue={setUsername}
                />
                <TextField
                  title={"*Password"}
                  direction={"column"}
                  type={"password"}
                  width={"100%"}
                  value={password}
                  setValue={handlePasswordChange}
                />
                <TextField
                  title={"*Verify Password"}
                  direction={"column"}
                  type={"password"}
                  width={"100%"}
                  value={verifyPassword}
                  setValue={setVerifyPassword}
                />
                <PasswordRequirements requirements={passwordRequirements} />
                <PasswordStrengthBar strength={strength} />
              </FieldsContainer>
              <ButtonContainer>
                <CustomButton handle={Submit} title="Submit" />
              </ButtonContainer>
            </LeftContaienrContent>
          </LeftContaienr>
        </InnerContainer>
      </Container>
      <Footer />
    </>
  );
}

const PasswordRequirements = ({ requirements }) => {
  return (
    <Requirements>
      <Requirement>
        {requirements.uppercase ? "✔️" : "❌"} At least one uppercase letter, one lowercase letter.
      </Requirement>
      <Requirement>
        {requirements.specialChar ? "✔️" : "❌"} At least one special character
        from @$!%*?&.
      </Requirement>
      <Requirement>
        {requirements.minLength ? "✔️" : "❌"} A minimum of 8 characters in
        total.
      </Requirement>
    </Requirements>
  );
};

const PasswordStrengthBar = ({ strength }) => {
  const getColor = (score) => {
    if (score <= 25) return "#FF0000"; // Weak
    if (score <= 50) return "#FFA500"; // Moderate
    if (score <= 75) return "#FFFF00"; // Good
    return "#008000"; // Strong
  };

  return (
    <ProgressBar>
      <Progress
        style={{ width: `${strength}%`, backgroundColor: getColor(strength) }}
      />
    </ProgressBar>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 95vh;
`;

const Requirements = styled.div`
  margin-top: 10px;
  display: flex;
  align-items:flex-start;
  justify-content:flex-start;
  flex-direction: column;
  gap: 5px;
`;

const Requirement = styled.div`
  font-family: "Museo Sans Cyrl";
  font-size: 14px;
  color: #304961;
`;

const InnerContainer = styled.div`
  padding: 100px 100px;
  min-height: 85vh;
  width: 90vw;
  border-radius: 30px;
  background-image: url(${womanDoctor});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: local;
  padding: 7%;
  
  
  @media screen and (max-width: 662px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
`;

const LeftContaienr = styled.div`
  display: flex;
  flex-direction: column;
  width: 30vw;
  min-width: 300px;
    border-radius: 20px;
  gap: 2vh;
  border-radius: 20px;
  background: #ecf3fb;
  

`;
const LeftContaienrBanner = styled.div`
display:flex;
width:auto;
background-color:#304961;
min-height:2vh;
padding:5%;
padding-bottom:8%;
padding-top:8%;


border-radius: 20px 20px 0 0;
align-items: center;
justify-content: center;
  box-shadow:0px 0px 10px #304961;


`;
const LeftContaienrContent = styled.div`
  display: flex;
  flex-direction: column;
    justify-content: center;

  align-items:flex-start;
  width: auto;
  gap: 2vh;
  background: #ecf3fb;
  padding:5%;
  border-radius:20px;

  @media screen and (max-width: 662px) {
    gap: 20px;
    padding: 20px 10px;
  }
`;


const Title = styled.p`
  margin-bottom: 20px;
  color: var(--white);
font-family: 'Georgia';
  font-size: 19px;
  font-style: normal;
  font-weight: 700;
  line-height: 90%;
  letter-spacing: 0.7px;
  white-space: wrap;
  text-transform: uppercase;


`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin-top: 10px;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  transition: width 0.3s ease-in-out;
`;



const Text = styled.p`
  color: var(--darkBlue);

  font-family: "Museo Sans Cyrl";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  padding: 20px 0px;
  width:100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
width:100%
`;

export default RequireLogout(SetupProfile);
